import { apiClient } from '@/api/client';

/**
 * Trusted Partner profile (FR-28, MYN-104). Matches the Backend
 * `UpdatePartnerProfileDto` / response shape exactly - `logoFileKey`/
 * `coverPhotoFileKey` are the S3 keys sent on save; `logoUrl`/`coverPhotoUrl`
 * are what the Backend echoes back for display (same key, different name,
 * mirroring the Backend service's own `logoUrl: logoFileKey` mapping).
 */
/** How members reach a Trusted Partner (FR-108). Exactly one is active; the inquiry email is always required. */
export type ContactPreference = 'email' | 'scheduling';

export interface PartnerProfileData {
  id?: string;
  businessName: string;
  contactName: string;
  contactTitle: string;
  /**
   * Read-only: the caller's own account email, seeded by the Backend on
   * first profile creation and never overwritten after (mirrors
   * `InvestorProfileData.email`). Never part of the PATCH payload -
   * `UpdatePartnerProfileDto` has no field for it, and the Backend's
   * `forbidNonWhitelisted` ValidationPipe rejects it outright if sent.
   */
  contactEmail?: string;
  /** "Contact Email for Inquiries" (data-fields.md) - distinct from contactEmail. */
  inquiryEmail: string;
  phone: string;
  websiteUrl: string;
  /** `null` clears the stored link on save (an empty string would leave it as stored). */
  schedulingUrl?: string | null;
  /** Which contact method the member-facing profile offers (FR-108). Defaults to `email`. */
  contactPreference?: ContactPreference;
  /** READ-ONLY: raw Schedule-button clicks on the current Scheduling link (FR-110); resets when the link changes. */
  schedulingClickCount?: number;
  /** READ-ONLY: raw clicks on the Contact (email) button of the member-facing profile (FR-118). */
  emailContactClickCount?: number;
  /** "Bio / Introduction" in data-fields.md. */
  description: string;
  /** READ-ONLY: presigned URLs the backend resolved from the stored keys. */
  logoUrl?: string;
  /** The DETAIL-page cover (FR-116). */
  coverPhotoUrl?: string;
  /** The optional directory-card cover (FR-116); null when none was added. */
  listingCoverPhotoUrl?: string | null;
  /**
   * WRITE-ONLY, and OPTIONAL because omitting is meaningful. Both columns are
   * NOT NULL, so the DTO pairs `@IsSubmitted()` with `@IsNotEmpty()`: omit a
   * key to leave the stored image alone, and an empty one is refused as an
   * attempted clear. These were required here, which forced the form to send
   * `''` for whichever image had not just been re-uploaded - so saving after
   * changing only one of them was rejected outright.
   */
  logoFileKey?: string;
  coverPhotoFileKey?: string;
  /** WRITE-ONLY and optional (FR-116): the listing cover's S3 key. Omit to leave the stored one alone. */
  listingCoverFileKey?: string | null;
  promoVideoUrl?: string;
  categoryIds: string[];
  tags?: string[];
  tagline?: string;
  linkedinUrl?: string;
  /**
   * "Featured Trusted Partner" (data-fields.md) - spotlights the partner on
   * the member dashboard and public homepage. Admin-only: the Backend
   * already returns this on every GET (a full row spread), but
   * `UpdatePartnerProfileDto` has no field for it, so a partner PATCH can
   * never set it. Read-only display in the FE profile view, never a form
   * field - self-service "feature me" would let every partner mark
   * themselves featured.
   */
  isFeatured?: boolean;
}

/**
 * The PATCH body: the profile with `phone` optional, because a legacy free-text phone the user
 * did not edit is omitted from the save (neither rejected by the API nor wiped; MYN-277).
 */
export type PartnerProfilePayload = Omit<PartnerProfileData, 'phone'> & { phone?: string };

/**
 * The Backend returns 200/`null` (not 404) when the entity has no profile
 * yet; coerced to `{}` here so `EntityProfileModule<T>.fetch` keeps its
 * non-nullable `Promise<T>` contract - `toFormValues` already defaults every
 * field via `??`, mirroring `fetchInvestorProfile`'s identical `data ?? {}`.
 */
export async function fetchPartnerProfile(entityId: string): Promise<PartnerProfileData> {
  const { data } = await apiClient.get<PartnerProfileData | null>(`/partners/${entityId}`);
  return data ?? ({} as PartnerProfileData);
}

export async function savePartnerProfile(
  entityId: string,
  payload: PartnerProfilePayload,
): Promise<PartnerProfileData> {
  const { data } = await apiClient.patch<PartnerProfileData>(`/partners/${entityId}`, payload);
  return data;
}

/**
 * `:entityId/profile`, NOT the bare `:entityId` - the Backend's admin
 * console (`PartnerProfilesController`, FR-71/MYN-103) owns
 * `DELETE /v1/partners/:entityId` for a different operation (permanent
 * entity deletion). This route only soft-deletes the profile CONTENT (see
 * `partner-profile.controller.ts`'s doc comment on the Backend).
 */
export async function deletePartnerProfile(entityId: string): Promise<void> {
  await apiClient.delete(`/partners/${entityId}/profile`);
}
